// Floating watchlist panel — fixed right edge, expands on hover.
// Groups stored in localStorage as meridian_watchlist_groups.
// Migrates automatically from old flat-array format.

import { esc } from './utils';
import { getWorkerBase } from './worker';
import { store } from './state';

const LS_GROUPS  = 'meridian_watchlist_groups';
const LS_LEGACY  = 'meridian_watchlist';
const DEFAULT_TICKERS = ['HAWK', 'PL', 'SATL', 'IONQ', 'KRMN', 'CRSP', 'CBRS'];

interface WatchGroup { id: string; name: string; tickers: string[]; collapsed: boolean; }
interface PriceData  { price: number; change: number; changePct: number; }

let groups: WatchGroup[] = [];
let targetGroupId        = '';          // which group [+] adds to
let expanded             = false;
let addingGroup          = false;       // inline group creation mode
let collapseTimer: ReturnType<typeof setTimeout> | null = null;
const priceCache         = new Map<string, PriceData>();

// ── Persistence ───────────────────────────────────────────────────────────────

function loadGroups(): WatchGroup[] {
  try {
    const stored = localStorage.getItem(LS_GROUPS);
    if (stored) return JSON.parse(stored) as WatchGroup[];
  } catch { /* fall through */ }

  // Migrate from old flat array
  try {
    const old = JSON.parse(localStorage.getItem(LS_LEGACY) ?? 'null') as string[] | null;
    if (old && old.length) {
      return [{ id: 'general', name: 'General', tickers: old, collapsed: false }];
    }
  } catch { /* fall through */ }

  return [{ id: 'general', name: 'General', tickers: [...DEFAULT_TICKERS], collapsed: false }];
}

function saveGroups(): void {
  localStorage.setItem(LS_GROUPS, JSON.stringify(groups));
  // Fire-and-forget sync to KV so Claude.ai MCP tools stay in sync
  const base  = getWorkerBase();
  const token = localStorage.getItem('meridian_auth_token') ?? '';
  if (base) {
    fetch(`${base}/api/watchlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      body: JSON.stringify({ groups }),
    }).catch(() => {});
  }
}

function allTickers(): string[] {
  return groups.flatMap(g => g.tickers);
}

function findGroupForTicker(ticker: string): WatchGroup | undefined {
  return groups.find(g => g.tickers.includes(ticker));
}

// ── Init ──────────────────────────────────────────────────────────────────────

export function initWatchlist(): void {
  groups = loadGroups();
  if (!targetGroupId && groups.length) targetGroupId = groups[0].id;
  buildDOM();
  wireEvents();

  // Sync from KV — Claude.ai may have added/removed tickers since last session
  const base  = getWorkerBase();
  const token = localStorage.getItem('meridian_auth_token') ?? '';
  if (base) {
    fetch(`${base}/api/watchlist`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      signal: AbortSignal.timeout(5000),
    })
    .then(r => r.ok ? r.json() as Promise<{ groups?: typeof groups }> : Promise.reject())
    .then(data => {
      if (data.groups && data.groups.length) {
        groups = data.groups;
        localStorage.setItem(LS_GROUPS, JSON.stringify(groups));
        if (!groups.find(g => g.id === targetGroupId)) targetGroupId = groups[0]?.id ?? '';
        renderList();
        updateTargetLabel();
      }
    })
    .catch(() => {});
  }

  // Sync portfolio holdings → "Portfolio" group
  store.on('tickers', (rows: any[]) => {
    const holderTickers = rows
      .map((r: any) => String(r.Ticker ?? r.Symbol ?? '').toUpperCase().trim())
      .filter(Boolean);
    if (!holderTickers.length) return;

    let portfolio = groups.find(g => g.id === 'portfolio');
    if (!portfolio) {
      portfolio = { id: 'portfolio', name: 'Portfolio', tickers: [], collapsed: false };
      groups.push(portfolio);
    }
    let changed = false;
    for (const t of holderTickers) {
      if (!portfolio.tickers.includes(t)) { portfolio.tickers.push(t); changed = true; }
    }
    if (changed) { saveGroups(); renderList(); }
  });
}

// ── DOM ───────────────────────────────────────────────────────────────────────

function buildDOM(): void {
  if (document.getElementById('floating-watchlist')) return;

  const el = document.createElement('div');
  el.id = 'floating-watchlist';
  el.style.cssText = 'position:fixed;right:0;top:50%;transform:translateY(-50%);z-index:8000;display:flex;flex-direction:row;align-items:stretch;';

  el.innerHTML = `
    <div id="fw-handle" style="background:var(--bg2);border:1px solid var(--border);border-right:none;border-radius:6px 0 0 6px;padding:12px 6px;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;writing-mode:vertical-rl;font-family:var(--mono);font-size:10px;letter-spacing:0.15em;color:var(--text3);text-transform:uppercase;user-select:none;transition:background 0.2s,color 0.2s;min-height:80px;" title="Watchlist">
      <span style="font-size:12px;">📋</span>
      <span>WATCH</span>
    </div>
    <div id="fw-panel" style="width:0;overflow:hidden;transition:width 0.3s cubic-bezier(0.4,0,0.2,1);background:var(--bg2);border:1px solid var(--border);border-right:none;border-radius:6px 0 0 6px;display:flex;flex-direction:column;">
      <!-- Header -->
      <div style="padding:10px 14px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;min-width:240px;">
        <span style="font-family:var(--sans);font-size:13px;font-weight:700;color:var(--accent);">Watchlist</span>
        <div style="display:flex;gap:6px;">
          <button id="fw-refresh-btn" style="background:none;border:none;color:var(--text3);cursor:pointer;font-size:11px;padding:2px 4px;" title="Refresh prices">↺</button>
          <button id="fw-close-btn"   style="background:none;border:none;color:var(--text3);cursor:pointer;font-size:13px;padding:2px 4px;" title="Close">✕</button>
        </div>
      </div>
      <!-- Add ticker bar -->
      <div style="padding:8px 10px;border-bottom:1px solid var(--border);flex-shrink:0;min-width:240px;">
        <div style="display:flex;gap:5px;margin-bottom:5px;">
          <input id="fw-add-input" type="text" placeholder="Add ticker…" autocomplete="off"
            style="flex:1;background:var(--panel);border:1px solid var(--border);color:var(--text);font-family:var(--mono);font-size:11px;padding:5px 8px;border-radius:3px;outline:none;text-transform:uppercase;min-width:0;">
          <button id="fw-add-btn" style="background:rgba(0,212,255,0.1);border:1px solid rgba(0,212,255,0.3);color:var(--accent);font-family:var(--mono);font-size:10px;padding:5px 8px;cursor:pointer;border-radius:3px;white-space:nowrap;">+ ADD</button>
        </div>
        <div style="display:flex;align-items:center;justify-content:space-between;gap:6px;">
          <div id="fw-target-label" style="font-size:10px;color:var(--text3);font-family:var(--mono);">→ General</div>
          <button id="fw-new-group-btn" style="background:none;border:1px solid var(--border);color:var(--text3);font-family:var(--mono);font-size:9px;padding:2px 7px;cursor:pointer;border-radius:2px;letter-spacing:0.06em;white-space:nowrap;">+ GROUP</button>
        </div>
        <div id="fw-new-group-row" style="display:none;margin-top:5px;gap:5px;">
          <input id="fw-new-group-input" type="text" placeholder="Group name…" autocomplete="off"
            style="flex:1;background:var(--panel);border:1px solid rgba(0,212,255,0.4);color:var(--text);font-family:var(--mono);font-size:11px;padding:4px 7px;border-radius:3px;outline:none;min-width:0;">
          <button id="fw-new-group-ok" style="background:rgba(0,212,255,0.1);border:1px solid rgba(0,212,255,0.3);color:var(--accent);font-family:var(--mono);font-size:10px;padding:4px 8px;cursor:pointer;border-radius:3px;">OK</button>
          <button id="fw-new-group-cancel" style="background:none;border:1px solid var(--border);color:var(--text3);font-family:var(--mono);font-size:10px;padding:4px 6px;cursor:pointer;border-radius:3px;">✕</button>
        </div>
      </div>
      <!-- List -->
      <div id="fw-list" style="flex:1;overflow-y:auto;min-width:240px;max-height:calc(100vh - 280px);"></div>
      <!-- Status bar -->
      <div style="padding:5px 10px;border-top:1px solid var(--border);flex-shrink:0;min-width:240px;">
        <div id="fw-status" style="font-size:10px;color:var(--text3);letter-spacing:0.08em;">—</div>
      </div>
    </div>`;

  document.body.appendChild(el);

  const style = document.createElement('style');
  style.textContent = `
    #fw-handle:hover { background:var(--bg3)!important; color:var(--accent)!important; }
    .fw-group-hd { display:flex;align-items:center;gap:6px;padding:6px 10px;cursor:pointer;background:var(--bg3);border-bottom:1px solid var(--border);user-select:none; }
    .fw-group-hd:hover { background:rgba(0,212,255,0.05); }
    .fw-group-chevron { font-size:9px;color:var(--text3);transition:transform 0.2s;width:12px;flex-shrink:0; }
    .fw-group-name { font-family:var(--mono);font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:var(--text2);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
    .fw-group-count { font-family:var(--mono);font-size:9px;color:var(--text3); }
    .fw-group-add { background:none;border:none;color:var(--text3);cursor:pointer;font-size:11px;padding:1px 4px;opacity:0;transition:opacity 0.15s; }
    .fw-group-hd:hover .fw-group-add { opacity:1; }
    .fw-ticker-row { display:flex;align-items:center;padding:7px 10px;border-bottom:1px solid rgba(30,52,72,0.4);cursor:pointer;transition:background 0.15s;gap:8px; }
    .fw-ticker-row:hover { background:rgba(0,212,255,0.06); }
    .fw-ticker-row:last-child { border-bottom:none; }
    .fw-remove-btn { opacity:0;transition:opacity 0.2s;background:none;border:none;color:var(--text3);cursor:pointer;font-size:12px;padding:2px 4px;flex-shrink:0; }
    .fw-ticker-row:hover .fw-remove-btn { opacity:1; }
    .fw-target { border-color:rgba(0,212,255,0.5)!important;color:var(--accent)!important; }
  `;
  document.head.appendChild(style);
}

// ── Events ────────────────────────────────────────────────────────────────────

function wireEvents(): void {
  document.getElementById('fw-handle')     ?.addEventListener('mouseenter', expand);
  document.getElementById('fw-close-btn')  ?.addEventListener('click', collapse);
  document.getElementById('fw-refresh-btn')?.addEventListener('click', () => { priceCache.clear(); refreshPrices(); });

  document.getElementById('fw-add-btn')?.addEventListener('click', commitAdd);
  (document.getElementById('fw-add-input') as HTMLInputElement | null)
    ?.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter') commitAdd(); });

  document.getElementById('fw-new-group-btn')?.addEventListener('click', showNewGroupInput);
  document.getElementById('fw-new-group-ok')?.addEventListener('click', commitNewGroup);
  document.getElementById('fw-new-group-cancel')?.addEventListener('click', hideNewGroupInput);
  (document.getElementById('fw-new-group-input') as HTMLInputElement | null)
    ?.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') commitNewGroup();
      if (e.key === 'Escape') hideNewGroupInput();
    });

  const fw = document.getElementById('floating-watchlist')!;
  fw.addEventListener('mouseleave', () => { collapseTimer = setTimeout(collapse, 800); });
  fw.addEventListener('mouseenter', () => { if (collapseTimer) { clearTimeout(collapseTimer); collapseTimer = null; } });
}

// ── Group management ──────────────────────────────────────────────────────────

function showNewGroupInput(): void {
  addingGroup = true;
  const row = document.getElementById('fw-new-group-row');
  if (row) row.style.display = 'flex';
  const input = document.getElementById('fw-new-group-input') as HTMLInputElement | null;
  if (input) { input.value = ''; input.focus(); }
}

function hideNewGroupInput(): void {
  addingGroup = false;
  const row = document.getElementById('fw-new-group-row');
  if (row) row.style.display = 'none';
}

function commitNewGroup(): void {
  const input = document.getElementById('fw-new-group-input') as HTMLInputElement | null;
  const name = (input?.value ?? '').trim();
  hideNewGroupInput();
  if (!name) return;
  const id = 'grp-' + Date.now();
  groups.push({ id, name, tickers: [], collapsed: false });
  targetGroupId = id;
  saveGroups();
  renderList();
  updateTargetLabel();
}

function toggleGroup(id: string): void {
  const g = groups.find(g => g.id === id);
  if (g) { g.collapsed = !g.collapsed; saveGroups(); renderList(); }
}

function setTargetGroup(id: string): void {
  targetGroupId = id;
  updateTargetLabel();
  document.getElementById('fw-add-input')?.focus();
}

function updateTargetLabel(): void {
  const g = groups.find(g => g.id === targetGroupId) ?? groups[0];
  const lbl = document.getElementById('fw-target-label');
  if (lbl && g) lbl.textContent = `→ ${g.name}`;
}

// ── CRUD ──────────────────────────────────────────────────────────────────────

function commitAdd(): void {
  const input = document.getElementById('fw-add-input') as HTMLInputElement | null;
  const ticker = (input?.value ?? '').toUpperCase().trim();
  if (!ticker || ticker.length < 1 || ticker.length > 10) return;
  if (input) input.value = '';
  addToGroup(ticker, targetGroupId || groups[0]?.id || '');
}

export function addTickerToWatchlist(ticker: string): void {
  addToGroup(ticker.toUpperCase().trim(), targetGroupId || groups[0]?.id || '');
}

function addToGroup(ticker: string, groupId: string): void {
  let g = groups.find(g => g.id === groupId);
  if (!g) { g = groups[0]; }
  if (!g) return;
  // Move from old group if already tracked elsewhere
  for (const other of groups) {
    if (other.id !== g.id) other.tickers = other.tickers.filter(t => t !== ticker);
  }
  if (!g.tickers.includes(ticker)) g.tickers.unshift(ticker);
  saveGroups();
  renderList();
  fetchPrice(ticker).then(() => renderList());
}

function removeTicker(ticker: string): void {
  for (const g of groups) g.tickers = g.tickers.filter(t => t !== ticker);
  saveGroups();
  renderList();
}

function openChart(ticker: string): void {
  (window as any).showTab?.('charts');
  (window as any).loadChartTicker?.(ticker);
}

// ── Expand / collapse ─────────────────────────────────────────────────────────

function expand(): void {
  if (collapseTimer) { clearTimeout(collapseTimer); collapseTimer = null; }
  if (expanded) return;
  expanded = true;
  const panel  = document.getElementById('fw-panel')!;
  const handle = document.getElementById('fw-handle')!;
  panel.style.width        = '260px';
  handle.style.background  = 'var(--bg3)';
  handle.style.color       = 'var(--accent)';
  renderList();
  refreshPrices();
}

function collapse(): void {
  expanded = false;
  const panel  = document.getElementById('fw-panel')!;
  const handle = document.getElementById('fw-handle')!;
  panel.style.width       = '0';
  handle.style.background = 'var(--bg2)';
  handle.style.color      = 'var(--text3)';
}

// ── Prices ────────────────────────────────────────────────────────────────────

async function fetchPrice(ticker: string): Promise<void> {
  const workerBase = getWorkerBase();
  if (!workerBase) return;
  try {
    const url   = `https://query1.finance.yahoo.com/v8/finance/chart/${ticker}?interval=1d&range=2d`;
    const proxy = `${workerBase}/api/proxy?url=${encodeURIComponent(url)}`;
    const res   = await fetch(proxy, { signal: AbortSignal.timeout(5000) });
    const raw   = await res.json() as { contents: string };
    const meta  = JSON.parse(raw.contents)?.chart?.result?.[0]?.meta;
    if (!meta) return;
    const price     = meta.regularMarketPrice as number;
    const prev      = (meta.chartPreviousClose ?? meta.regularMarketPreviousClose) as number;
    const change    = price - prev;
    const changePct = (change / prev) * 100;
    priceCache.set(ticker, { price, change, changePct });
  } catch { /* silent */ }
}

async function refreshPrices(): Promise<void> {
  const status = document.getElementById('fw-status');
  if (status) status.textContent = 'Fetching prices…';
  const all = allTickers();
  for (let i = 0; i < all.length; i += 3) {
    await Promise.all(all.slice(i, i + 3).map(t => fetchPrice(t)));
    renderList();
  }
  if (status) status.textContent = `Updated ${new Date().toLocaleTimeString()}`;
}

// ── Render ────────────────────────────────────────────────────────────────────

function renderList(): void {
  const list = document.getElementById('fw-list');
  if (!list) return;

  if (!groups.length || allTickers().length === 0) {
    list.innerHTML = '<div style="padding:20px;text-align:center;color:var(--text3);font-size:11px;">Add tickers above to track them</div>';
    return;
  }

  // Ensure target group is valid
  if (!groups.find(g => g.id === targetGroupId)) {
    targetGroupId = groups[0].id;
    updateTargetLabel();
  }

  list.innerHTML = groups.map(g => {
    const isTarget = g.id === targetGroupId;
    const hd = `
      <div class="fw-group-hd${isTarget ? ' fw-target' : ''}" data-fw-group="${esc(g.id)}">
        <span class="fw-group-chevron" style="transform:${g.collapsed ? 'rotate(-90deg)' : 'none'}">${g.collapsed ? '▶' : '▾'}</span>
        <span class="fw-group-name">${esc(g.name)}</span>
        ${g.collapsed ? `<span class="fw-group-count">(${g.tickers.length})</span>` : ''}
        <button class="fw-group-add" data-fw-target="${esc(g.id)}" title="Add ticker to ${esc(g.name)}">＋</button>
      </div>`;

    if (g.collapsed) return hd;

    const rows = g.tickers.length
      ? g.tickers.map(ticker => {
          const p         = priceCache.get(ticker);
          const priceStr  = p ? `$${p.price.toFixed(2)}` : '—';
          const changeStr = p ? `${p.changePct >= 0 ? '+' : ''}${p.changePct.toFixed(2)}%` : '';
          const col       = p ? (p.changePct >= 0 ? 'var(--green)' : 'var(--red)') : 'var(--text3)';
          const arrow     = p ? (p.changePct >= 0 ? '▲' : '▼') : '';
          const sparkY1   = p ? (p.changePct >= 0 ? 15 : 5) : 10;
          const sparkY2   = p ? (p.changePct >= 0 ? 5 : 15) : 10;

          return `<div class="fw-ticker-row" data-fw-ticker="${esc(ticker)}">
            <div style="flex:1;min-width:0;">
              <div style="font-family:var(--mono);font-size:12px;font-weight:700;color:var(--accent);">${esc(ticker)}</div>
              ${p
                ? `<div style="font-size:11px;color:${col};margin-top:1px;">${arrow} ${changeStr}</div>`
                : '<div style="font-size:10px;color:var(--text3);">loading…</div>'}
            </div>
            <div style="text-align:right;flex-shrink:0;">
              <div style="font-family:var(--mono);font-size:12px;color:var(--text);font-weight:${p ? '700' : '400'};">${priceStr}</div>
              ${p ? `<div style="font-size:10px;color:var(--text3);">${p.change >= 0 ? '+' : ''}$${Math.abs(p.change).toFixed(2)}</div>` : ''}
            </div>
            <div style="width:36px;flex-shrink:0;">
              ${p ? `<svg viewBox="0 0 36 20" style="width:36px;height:20px;"><line x1="0" y1="${sparkY1}" x2="36" y2="${sparkY2}" stroke="${col}" stroke-width="1.5" stroke-linecap="round"/></svg>` : ''}
            </div>
            <button class="fw-remove-btn" data-fw-remove="${esc(ticker)}"
              onmouseover="this.style.color='var(--red)'" onmouseout="this.style.color='var(--text3)'">✕</button>
          </div>`;
        }).join('')
      : `<div style="padding:8px 10px;font-size:10px;color:var(--text3);font-family:var(--mono);">Empty — click ＋ to add</div>`;

    return hd + rows;
  }).join('');

  // Event delegation
  list.onclick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;

    const removeBtn = target.closest<HTMLElement>('[data-fw-remove]');
    if (removeBtn) { e.stopPropagation(); removeTicker(removeBtn.dataset.fwRemove ?? ''); return; }

    const groupAddBtn = target.closest<HTMLElement>('[data-fw-target]');
    if (groupAddBtn) { e.stopPropagation(); setTargetGroup(groupAddBtn.dataset.fwTarget ?? ''); return; }

    const groupHd = target.closest<HTMLElement>('[data-fw-group]');
    if (groupHd && !target.closest('button')) { toggleGroup(groupHd.dataset.fwGroup ?? ''); return; }

    const row = target.closest<HTMLElement>('[data-fw-ticker]');
    if (row) openChart(row.dataset.fwTicker ?? '');
  };
}
